import {
  Badge,
  EmptyState,
  EnvBadge,
  Pagination,
  Skeleton,
  Table,
  type TableColumn,
  UserInfo,
  When,
  createPagedList,
} from '@/components';
import { A } from '@solidjs/router';
import type { ReleaseRow, ReleaseStatus } from '@uchat-team-management/shared';
import { For, Show, createMemo } from 'solid-js';
import type { ReleasesFilters } from './hooks';
import { useReleasesList } from './hooks';

export const STATUS_TONE: Record<
  ReleaseStatus,
  'neutral' | 'success' | 'warning' | 'danger' | 'info'
> = {
  draft: 'neutral',
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
  publishing: 'info',
  published: 'success',
};

export const ReleaseStatusBadge = (props: { status: ReleaseStatus; stale?: boolean }) => (
  <span class="inline-flex items-center gap-1">
    <Badge tone={STATUS_TONE[props.status]}>{props.status}</Badge>
    <Show when={props.stale}>
      <Badge tone="warning">stale</Badge>
    </Show>
  </span>
);

interface Props {
  filters: () => ReleasesFilters;
  groupBy?: string;
}

export const ReleasesTable = (props: Props) => {
  const query = useReleasesList(props.filters);
  const { rows, page, totalPages, jumping, goToPage } = createPagedList(query, props.filters);

  const groups = createMemo(() => {
    const key = props.groupBy;
    if (key !== 'version' && key !== 'environment') return [];
    const grouped = new Map<string, ReleaseRow[]>();
    for (const row of rows()) {
      const label = key === 'version' ? row.version : row.environment.toUpperCase();
      const group = grouped.get(label) ?? [];
      group.push(row);
      grouped.set(label, group);
    }
    return [...grouped];
  });

  // A single-product / single-environment filter makes that info redundant — hide it.
  const columns = (): readonly TableColumn<ReleaseRow>[] => [
    {
      key: 'version',
      header: 'Version',
      render: (row) => (
        <>
          <Show when={!props.filters().productId}>
            <p class="text-xs text-neutral-500">{row.productName}</p>
          </Show>
          <A
            href={`/release/versions/${row.id}`}
            class="font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            {row.version} <span class="text-neutral-400">#{row.buildNumber}</span>
          </A>
          <Show when={row.description}>
            <p class="whitespace-pre-wrap text-xs text-neutral-500">{row.description}</p>
          </Show>
        </>
      ),
    },
    ...(props.filters().environment
      ? []
      : [
          {
            key: 'environment',
            header: 'Environment',
            render: (row: ReleaseRow) => <EnvBadge env={row.environment} />,
          },
        ]),
    {
      key: 'status',
      header: 'Status',
      render: (row) => <ReleaseStatusBadge status={row.status} stale={row.isStale} />,
    },
    { key: 'createdBy', header: 'Created by', render: (row) => <UserInfo user={row.createdBy} /> },
    {
      key: 'createdAt',
      header: 'Created',
      render: (row) => <When date={row.createdAt} class="text-neutral-500" />,
    },
  ];

  return (
    <Show when={!query.isPending} fallback={<Skeleton class="h-40" />}>
      <Show
        when={query.data}
        fallback={<EmptyState variant="error" title="Failed to load releases" />}
      >
        <Show
          when={groups().length > 0}
          fallback={
            <Table
              columns={columns()}
              data={rows()}
              rowKey={(row) => row.id}
              loading={jumping()}
              empty={<EmptyState title="No releases yet" body="Create a draft to get started." />}
            />
          }
        >
          <p class="mb-3 text-xs text-neutral-500">Grouped within this page</p>
          <For each={groups()}>
            {([label, data]) => (
              <section class="mb-4">
                <h2 class="mb-2 font-semibold">
                  {label} <span class="text-neutral-500">({data.length})</span>
                </h2>
                <Table
                  columns={columns()}
                  data={data}
                  rowKey={(row) => row.id}
                  loading={jumping()}
                />
              </section>
            )}
          </For>
        </Show>
        <Pagination
          class="mt-4"
          page={page()}
          totalPages={totalPages()}
          loading={jumping()}
          onPage={goToPage}
        />
      </Show>
    </Show>
  );
};
